import { useEffect, useState } from "react";
import {
    Avatar,
    Box,
    Button,
    CircularProgress,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from "@mui/material";
import CustomContainer from "../../components/CustomContainer";
import PageHeader from "../../components/PageHeader";
import FormSection from "../../components/ui/FormSection";
import Eyebrow from "../../components/ui/Eyebrow";
import LoadingComponent from "../../components/LoadingComponent";
import { useAuth } from "../../contexts/AuthProvider";
import { FaFloppyDisk, FaLock, FaCamera } from "react-icons/fa6";
import { Link } from "react-router-dom";
import { TextMaskCEP, TextMaskPhone } from "../../components/MaskedInputs";
import { updateCustomerProfile } from "../../helpers/httpRequests/customer";
import appToast from "../../components/appToast";

const STATES = [
    "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA",
    "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN",
    "RS", "RO", "RR", "SC", "SP", "SE", "TO",
];

const EMPTY_FORM = {
    whatsapp_number: "",
    email: "",
    main_manager: "",
    main_manager_contact: "",
    financial_manager: "",
    financial_manager_contact: "",
    zip_code: "",
    address: "",
    address_number: "",
    complement: "",
    neighborhood: "",
    city: "",
    state: "",
};

function fileToSquareJpeg(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error("Não foi possível ler a imagem."));
        reader.onload = (event) => {
            const img = new Image();
            img.onerror = () => reject(new Error("Arquivo de imagem inválido."));
            img.onload = () => {
                const canvas = document.createElement("canvas");
                const size = 256;
                canvas.width = size;
                canvas.height = size;
                const ctx = canvas.getContext("2d");
                const aspectRatio = img.width / img.height;
                let drawWidth;
                let drawHeight;
                let offsetX;
                let offsetY;

                if (aspectRatio > 1) {
                    drawWidth = size * aspectRatio;
                    drawHeight = size;
                    offsetX = -(drawWidth - size) / 2;
                    offsetY = 0;
                } else {
                    drawWidth = size;
                    drawHeight = size / aspectRatio;
                    offsetX = 0;
                    offsetY = -(drawHeight - size) / 2;
                }

                ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
                resolve(canvas.toDataURL("image/jpeg"));
            };
            img.src = event.target.result;
        };
        reader.readAsDataURL(file);
    });
}

function ProfileField({
    name,
    label,
    value,
    onChange,
    required = true,
    disabled = false,
    mask,
    placeholder,
    onBlur,
    numeric = false,
}) {
    return (
        <TextField
            fullWidth
            required={required && !disabled}
            disabled={disabled}
            name={name}
            label={label}
            value={value == null ? "" : String(value)}
            onChange={onChange}
            onBlur={onBlur}
            placeholder={placeholder}
            slotProps={{
                inputLabel: { sx: { color: "text.secondary" } },
                htmlInput: numeric ? { sx: { fontVariantNumeric: "tabular-nums" } } : undefined,
                input: mask ? { inputComponent: mask } : undefined,
            }}
        />
    );
}

export default function MyProfile() {
    const { user, refreshUser } = useAuth();
    const [form, setForm] = useState(EMPTY_FORM);
    const [avatar, setAvatar] = useState("");
    const [avatarDirty, setAvatarDirty] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!user) return;
        const text = (value) => (value == null || value === "" ? "" : String(value));
        setForm({
            whatsapp_number: text(user.whatsapp_number),
            email: text(user.email),
            main_manager: text(user.main_manager),
            main_manager_contact: text(user.main_manager_contact),
            financial_manager: text(user.financial_manager),
            financial_manager_contact: text(user.financial_manager_contact),
            zip_code: text(user.zip_code),
            address: text(user.address),
            address_number: text(user.address_number),
            complement: text(user.complement),
            neighborhood: text(user.neighborhood),
            city: text(user.city),
            state: text(user.state),
        });
        setAvatar(user.image || "");
        setAvatarDirty(false);
    }, [user]);

    if (!user) {
        return <LoadingComponent />;
    }

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleAvatarChange = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        try {
            const next = await fileToSquareJpeg(file);
            setAvatar(next);
            setAvatarDirty(true);
        } catch (error) {
            appToast.error(error.message || "Não foi possível atualizar a foto.");
        }
    };

    const fetchAddressByCep = async (cep) => {
        const digits = String(cep || "").replace(/\D/g, "");
        if (digits.length !== 8) return;
        try {
            const response = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
            const data = await response.json();
            if (data.erro) {
                appToast.error("CEP não encontrado.");
                return;
            }
            setForm((prev) => ({
                ...prev,
                address: data.logradouro || prev.address,
                neighborhood: data.bairro || prev.neighborhood,
                city: data.localidade || prev.city,
                state: data.uf || prev.state,
            }));
        } catch {
            appToast.error("Não foi possível buscar o CEP.");
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = { ...form };
            if (avatarDirty && avatar && avatar.startsWith("data:image")) {
                payload.image = avatar;
            }
            await updateCustomerProfile(payload);
            if (refreshUser) {
                await refreshUser();
            }
            appToast.success("Perfil salvo.");
        } catch (error) {
            appToast.error(error.response?.data?.message || "Não foi possível salvar o perfil.");
        } finally {
            setSaving(false);
        }
    };

    const companyInitial = (user.complete_name || "C").slice(0, 1).toUpperCase();

    return (
        <CustomContainer>
            <Box component="form" onSubmit={handleSubmit} autoComplete="off" sx={{ py: 4 }}>
                <PageHeader title="Meu perfil" crumbs={["Meu perfil"]} />

                <Eyebrow sx={{ mb: 1 }}>Conta da empresa</Eyebrow>
                <Typography sx={styles.lede}>
                    Atualize contato, responsáveis e endereço. Dados fiscais ficam como no cadastro.
                </Typography>

                <FormSection title="Empresa">
                    <Box sx={styles.identity}>
                        <Avatar
                            src={avatar}
                            alt={user.complete_name || "Logo da empresa"}
                            sx={styles.avatar}
                        >
                            {companyInitial}
                        </Avatar>
                        <Box>
                            <Typography variant="h4" sx={{ textAlign: "left", mb: 1 }}>
                                {user.complete_name || "Sua empresa"}
                            </Typography>
                            <Typography sx={styles.hint}>
                                Foto quadrada, até 1 MB. CNPJ e razão social não podem ser alterados por aqui.
                            </Typography>
                            <Button
                                component="label"
                                variant="outlined"
                                color="primary"
                                startIcon={<FaCamera />}
                                sx={{ mt: 2 }}
                            >
                                Alterar foto
                                <input
                                    type="file"
                                    accept="image/*"
                                    hidden
                                    onChange={handleAvatarChange}
                                />
                            </Button>
                        </Box>
                    </Box>
                    <Grid container spacing={3} sx={{ mt: 1 }}>
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <ProfileField disabled label="Nome" value={user.complete_name} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <ProfileField disabled numeric label="CNPJ" value={user.cnpj} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <ProfileField disabled label="Inscrição estadual" value={user.state_registration} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <ProfileField disabled label="Razão social" value={user.social_reason} />
                        </Grid>
                    </Grid>
                </FormSection>

                <FormSection title="Contato">
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <ProfileField
                                numeric
                                name="whatsapp_number"
                                label="WhatsApp da empresa"
                                placeholder="(99) 99999-9999"
                                value={form.whatsapp_number}
                                onChange={handleChange}
                                mask={TextMaskPhone}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <ProfileField
                                name="email"
                                label="E-mail institucional"
                                value={form.email}
                                onChange={handleChange}
                            />
                        </Grid>
                    </Grid>
                </FormSection>

                <FormSection title="Responsável geral">
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <ProfileField
                                name="main_manager"
                                label="Nome completo"
                                value={form.main_manager}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <ProfileField
                                numeric
                                name="main_manager_contact"
                                label="WhatsApp"
                                placeholder="(99) 99999-9999"
                                value={form.main_manager_contact}
                                onChange={handleChange}
                                mask={TextMaskPhone}
                            />
                        </Grid>
                    </Grid>
                </FormSection>

                <FormSection title="Responsável financeiro">
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <ProfileField
                                name="financial_manager"
                                label="Nome completo"
                                value={form.financial_manager}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <ProfileField
                                numeric
                                name="financial_manager_contact"
                                label="WhatsApp"
                                placeholder="(99) 99999-9999"
                                value={form.financial_manager_contact}
                                onChange={handleChange}
                                mask={TextMaskPhone}
                            />
                        </Grid>
                    </Grid>
                </FormSection>

                <FormSection title="Endereço" sx={{ mb: 3 }}>
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <ProfileField
                                numeric
                                name="zip_code"
                                label="CEP"
                                placeholder="00000-000"
                                value={form.zip_code}
                                onChange={handleChange}
                                onBlur={() => fetchAddressByCep(form.zip_code)}
                                mask={TextMaskCEP}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <ProfileField
                                name="address"
                                label="Rua"
                                value={form.address}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <ProfileField
                                numeric
                                name="address_number"
                                label="Número"
                                value={form.address_number}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <ProfileField
                                required={false}
                                name="complement"
                                label="Complemento"
                                value={form.complement}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <ProfileField
                                name="neighborhood"
                                label="Bairro"
                                value={form.neighborhood}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <ProfileField
                                name="city"
                                label="Cidade"
                                value={form.city}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <FormControl fullWidth required>
                                <InputLabel sx={{ color: "text.secondary" }}>Estado</InputLabel>
                                <Select
                                    label="Estado"
                                    name="state"
                                    value={form.state}
                                    onChange={handleChange}
                                >
                                    {STATES.map((uf) => (
                                        <MenuItem key={uf} value={uf}>
                                            {uf}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </FormSection>

                <Box sx={styles.actions}>
                    <Button
                        component={Link}
                        to="/redefinir-senha"
                        variant="outlined"
                        color="primary"
                        startIcon={<FaLock />}
                    >
                        Alterar senha
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        color="secondary"
                        disabled={saving}
                        startIcon={saving ? <CircularProgress size={16} /> : <FaFloppyDisk />}
                    >
                        {saving ? "Salvando..." : "Salvar"}
                    </Button>
                </Box>
            </Box>
        </CustomContainer>
    );
}

const styles = {
    lede: {
        mb: 3,
        color: "text.secondary",
        textAlign: "left",
        maxWidth: 560,
    },
    identity: {
        display: "flex",
        alignItems: "center",
        gap: 3,
        flexWrap: "wrap",
    },
    avatar: {
        width: 128,
        height: 128,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.default",
        color: "text.primary",
        fontFamily: "DM Serif Display",
        fontSize: "2.5rem",
    },
    hint: {
        color: "text.secondary",
        textAlign: "left",
        maxWidth: 420,
    },
    actions: {
        display: "flex",
        justifyContent: "flex-end",
        flexWrap: "wrap",
        gap: 2,
    },
};
